import React, { useState, useEffect } from 'react'
import { Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react'
import { getCdnStatus, switchCdn } from '../api/fancaps'

interface CdnStatus {
  use_small_cdn_only: boolean
  current_cdn: string
  status: string
}

const CdnSwitcher: React.FC = () => {
  const [cdnStatus, setCdnStatus] = useState<CdnStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [switching, setSwitching] = useState(false)

  // 获取CDN状态
  const fetchCdnStatus = async () => {
    try {
      setLoading(true)
      const status = await getCdnStatus()
      setCdnStatus(status)
    } catch (error) {
      console.error('获取CDN状态失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 切换CDN
  const handleSwitch = async (useSmallCdn: boolean) => {
    try {
      setSwitching(true)
      await switchCdn(useSmallCdn)
      await fetchCdnStatus() // 刷新状态
    } catch (error) {
      console.error('CDN切换失败:', error)
    } finally {
      setSwitching(false)
    }
  }

  useEffect(() => {
    fetchCdnStatus()
  }, [])

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          <span>加载CDN状态...</span>
        </div>
      </div>
    )
  }

  if (!cdnStatus) {
    return null
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center">
          <Wifi className="w-5 h-5 mr-2" />
          CDN切换控制
        </h3>
        <button
          onClick={fetchCdnStatus}
          className="text-gray-500 hover:text-gray-700"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="space-y-4">
        {/* 当前状态 */}
        <div className="bg-gray-50 rounded-md p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">当前CDN:</span>
            <span className="font-medium">{cdnStatus.current_cdn}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-gray-600">状态:</span>
            <span className={`text-sm ${
              cdnStatus.use_small_cdn_only ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {cdnStatus.status}
            </span>
          </div>
        </div>

        {/* 切换按钮 */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleSwitch(true)}
            disabled={switching || cdnStatus.use_small_cdn_only}
            className={`flex items-center justify-center p-3 rounded-md border transition-colors ${
              cdnStatus.use_small_cdn_only
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-white border-gray-200 hover:bg-green-50 hover:border-green-200'
            }`}
          >
            <Wifi className="w-4 h-4 mr-2" />
            <div className="text-left">
              <div className="text-sm font-medium">小图CDN</div>
              <div className="text-xs text-gray-500">ant.fancaps.net</div>
            </div>
          </button>

          <button
            onClick={() => handleSwitch(false)}
            disabled={switching || !cdnStatus.use_small_cdn_only}
            className={`flex items-center justify-center p-3 rounded-md border transition-colors ${
              !cdnStatus.use_small_cdn_only
                ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                : 'bg-white border-gray-200 hover:bg-yellow-50 hover:border-yellow-200'
            }`}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            <div className="text-left">
              <div className="text-sm font-medium">大图CDN</div>
              <div className="text-xs text-gray-500">cdni.fancaps.net</div>
            </div>
          </button>
        </div>

        {switching && (
          <div className="text-center text-sm text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
            正在切换CDN...
          </div>
        )}

        {/* 警告信息 */}
        {!cdnStatus.use_small_cdn_only && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex items-start">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 mr-2" />
              <div className="text-sm text-yellow-700">
                <div className="font-medium">大图CDN风险提醒</div>
                <div>cdni.fancaps.net 容易触发rate limit，建议优先使用小图CDN</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CdnSwitcher 